import type {
  NewTicketAttachment,
  NewTicketPayload,
  NewTicketStep,
  TicketTag,
  TicketTagGroup
} from './types';

import {Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {routes} from 'routes/routes';
import {useAppDispatch, useAppSelector} from 'store';
import {getTags, newTicket} from 'store/slices/ticket/thunk';
import {getSelectedVendorFromStorage, sanitizeRedirect} from 'utils/Helper';

import NewTicketFooter from './components/NewTicketFooter/NewTicketFooter';
import TicketDetailsForm from './components/TicketDetailsForm/TicketDetailsForm';
import TicketTopicSelector from './components/TicketTopicSelector/TicketTopicSelector';

const NewTicket: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';
  const {tags, loading} = useAppSelector((state) => state.ticket);
  const [step, setStep] = useState<NewTicketStep>('question');
  const [selectedTag, setSelectedTag] = useState<TicketTag | null>(null);
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<NewTicketAttachment[]>([]);

  const orderId = searchParams.get('order_id');
  const orderCode = searchParams.get('order_code');
  const type = searchParams.get('type');

  const ticketsUrl = useMemo(() => {
    const ticketsQuery = new URLSearchParams();
    for (const key of ['order_code', 'type', 'state']) {
      const value = searchParams.get(key);
      if (value) {
        ticketsQuery.set(key, value);
      }
    }

    const from = searchParams.get('from');
    if (from) {
      ticketsQuery.set('from', sanitizeRedirect(from, routes.TICKETS));
    }

    const search = ticketsQuery.toString();
    return search ? `${routes.TICKETS}?${search}` : routes.TICKETS;
  }, [searchParams]);

  useEffect(() => {
    void dispatch(
      getTags({
        vendor_id: vendorId,
        type: type || ['general', 'loan']
      })
    );
  }, [dispatch, type, vendorId]);

  const handleCancel = () => {
    if (step === 'details') {
      setStep('question');
      return;
    }
    navigate(ticketsUrl);
  };

  const handleSubmit = async () => {
    if (!selectedTag) {
      return;
    }

    const payload: NewTicketPayload = {
      tag_id: selectedTag.id,
      description: comment,
      vendor_id: vendorId,
      file_ids: files.map((file) => file.file_key),
      ...(orderId ? {order_id: orderId} : {}),
      ...(orderCode ? {order_code: orderCode} : {})
    };

    try {
      await dispatch(newTicket(payload)).unwrap();
      toast('تیکت با موفقیت ثبت شد');
      navigate(ticketsUrl);
    } catch {
      toast('ثبت درخواست با خطا مواجه شد.');
    }
  };

  const handleContinue = () => {
    if (step === 'question') {
      setStep('details');
      return;
    }
    void handleSubmit();
  };

  const isLoading = Number(loading) > 0;
  const canContinue =
    step === 'question' ? selectedTag !== null : selectedTag !== null && comment.length > 10;
  const ticketTagGroups = Array.isArray(tags) ? (tags as TicketTagGroup[]) : [];

  return (
    <PageLayout
      footerPosition="static"
      footer={
        <NewTicketFooter
          loading={isLoading}
          canContinue={canContinue}
          onFaq={() => navigate('/faq')}
          onCancel={handleCancel}
          onContinue={handleContinue}
        />
      }
      footerClassName="h-auto! border-0! p-0!"
    >
      {isLoading && ticketTagGroups.length === 0 ? (
        <div className="flex min-h-60 flex-1 items-center justify-center">
          <Loading size="large" color="primary" />
        </div>
      ) : step === 'question' ? (
        <TicketTopicSelector
          groups={ticketTagGroups}
          selectedTag={selectedTag}
          onSelect={setSelectedTag}
        />
      ) : selectedTag ? (
        <TicketDetailsForm
          vendorId={vendorId}
          selectedTag={selectedTag}
          comment={comment}
          files={files}
          onCommentChange={setComment}
          onUploadSuccess={(file) => setFiles((currentFiles) => [...currentFiles, file])}
          onRemoveFile={(file) =>
            setFiles((currentFiles) =>
              currentFiles.filter((currentFile) => currentFile.file_key !== file.file_key)
            )
          }
        />
      ) : null}
    </PageLayout>
  );
};

export default NewTicket;
